import React, { FC, useState } from "react";
import { Form, Formik } from "formik";
import axios from "axios";
import { ObjectSchema } from "yup";
import { authAxios } from "api-generate-types/src/axios";
import { AlertMessage } from "../../Molecules/AlertMessage/AlertMessage";
import { Spinner } from "../../Atoms/Spinner/Spinner";
import Portal from "../../Atoms/Portal";
import { Popup } from "../../Molecules/Popup/Popup";
import { TFunction, useTranslation } from "i18n";
import { TextInputFormik } from "../../Atoms/FormikInputs/TextInputFormik/TextInputFormik";
import RichTextAreaFormik from "../../Atoms/FormikInputs/RichTextAreaFormik";
import { CheckBoxFormik } from "../../Atoms/FormikInputs/CheckboxFormik/CheckboxFormik";
import {
  MAX_MESSAGE_LENGTH,
  validationSchema as defaultValidationSchema,
} from "./validationSchema";
import { IDropzoneUploaderFile } from "../../Molecules/DropzoneUploader/DropzoneUploader";
import { DropzoneFormik } from "../../Atoms/FormikInputs/DropzoneFormik/DropzoneFormik";
import { config } from "../../../config";
import {
  NotificationToast,
  NotificationToastTypes,
} from "../../Atoms/NotificationToast/NotificationToast";
import { DOMPurify as DOMPurifyWithTargetBlankHook } from "../../Molecules/RichTextArea/utils";
import "./PerformerContact.scss";
import { dataTestIds } from "data-testids";

const MAX_FILE_SIZE = 5;

export interface IPerformerContactProps {
  closeForm: () => void;
  performerName: string;
  senderName: string;
  senderEmail: string;
  performerId: string;
  isOpen?: boolean;
  canAttachFiles?: boolean;
  submitformEndpoint?: string;
  emailSubjectLine?: string;
  customValidationSchema?: (t?: TFunction) => ObjectSchema<any>;
  successCallback?: () => void;
}

export interface IPerformerContactForm {
  message: string;
  subject: string;
  senderName: string;
  senderEmail: string;
  senderTelephoneNumber: string;
  companyName: string;
  companyWebsite: string;
  consent: boolean;
  attachments: IDropzoneUploaderFile[];
}

interface NotificationToastState {
  isOpen: boolean;
  type: NotificationToastTypes;
}

export enum SubmitFormEndpoint {
  SelfRep = "selfrepresented",
  LookingForRep = "lookingforrepresentation",
}

const PerformerContact: FC<React.PropsWithChildren<IPerformerContactProps>> = ({
  performerName,
  senderEmail,
  senderName,
  closeForm,
  performerId,
  isOpen,
  canAttachFiles = true,
  submitformEndpoint = SubmitFormEndpoint.SelfRep,
  emailSubjectLine,
  customValidationSchema,
  successCallback,
}) => {
  const { t } = useTranslation();
  const [showLoader, setShowLoader] = useState(false);
  const [notificationToast, setNotificationToast] =
    useState<NotificationToastState>({
      isOpen: false,
      type: NotificationToastTypes.Success,
    });

  const validationSchema = customValidationSchema ?? defaultValidationSchema;

  const submitContactPerformer = async (values: IPerformerContactForm) => {
    setShowLoader(true);

    try {
      await authAxios.post(
        `${config.profileApiUrl}/profiles/${performerId}/${submitformEndpoint}`,
        {
          ...values,
          message: DOMPurifyWithTargetBlankHook.sanitize(values.message),
          attachments: values.attachments.map(({ file: { name } }) => name),
        }
      );

      setNotificationToast({
        isOpen: true,
        type: NotificationToastTypes.Success,
      });

      if (successCallback) {
        successCallback();
      }
    } catch (e) {
      setNotificationToast({
        isOpen: true,
        type: NotificationToastTypes.Error,
      });
    }
    closeForm();
    setShowLoader(false);
  };

  const uploadFiles = async (files: IDropzoneUploaderFile[]): Promise<void> => {
    const { data } = await authAxios.post<{ url: string }[]>(
      `${config.profileApiUrl}/profiles/${performerId}/selfrepresented/attachments`,
      files.map((file) => file.file.name)
    );

    await Promise.all(
      data.map(async ({ url }, id) => {
        return await axios.put(url, files[id].file);
      })
    );
  };

  const subject = emailSubjectLine ?? t("common:contactPerformerModal.subject");

  const initialValues = {
    message: "",
    subject,
    senderName,
    senderEmail: senderEmail || "",
    senderTelephoneNumber: "",
    companyWebsite: "",
    companyName: "",
    consent: false,
    attachments: [],
  };

  const notificationText =
    notificationToast.type === "error"
      ? t("common:contactPerformerModal.failed")
      : t("common:contactPerformerModal.success");

  return (
    <Portal>
      <>
        {isOpen && (
          <Popup
            width={{ lg: 6, md: 8 }}
            priority="medium"
            close={closeForm}
            texts={{ closePopup: "" }}
          >
            <Formik<IPerformerContactForm>
              initialValues={initialValues}
              validationSchema={validationSchema(t)}
              onSubmit={submitContactPerformer}
            >
              {({ submitForm }) => {
                return (
                  <div
                    className="c-performer-contact"
                    data-testid={
                      dataTestIds.nuLinkBoard["ContactPerformerPopUp.Container"]
                    }
                  >
                    <AlertMessage
                      texts={{
                        title: `${t(
                          "common:contactPerformerModal.formHeader.colon"
                        )} ${performerName}`,
                      }}
                      buttons={[
                        {
                          name: t("common:button.label.send"),
                          type: "primary",
                          click: submitForm,
                        },
                        {
                          name: t("common:button.label.cancel"),
                          type: "secondary",
                          click: closeForm,
                        },
                      ]}
                    >
                      <Form className="c-performer-contact-form">
                        <div className="c-performer-contact-form__additional-text">
                          {t("common:contactPerformerModal.additionalText")}
                        </div>
                        <TextInputFormik
                          name="senderName"
                          label={t("common:label.yourName.colon")}
                        />
                        <TextInputFormik
                          name="senderEmail"
                          label={t("common:label.yourEmail.colon")}
                        />
                        <TextInputFormik
                          name="senderTelephoneNumber"
                          label={t(
                            "common:contactPerformerModal.yourContactNumberOptional.colon"
                          )}
                        />
                        <TextInputFormik
                          name="companyName"
                          label={t(
                            "common:contactPerformerModal.companyName.colon"
                          )}
                        />
                        <TextInputFormik
                          name="companyWebsite"
                          label={t(
                            "common:contactPerformerModal.companyWebsiteSocialMedia.colon"
                          )}
                        />
                        <RichTextAreaFormik
                          name="message"
                          label={t(
                            "common:contactPerformerModal.yourMessage.colon"
                          )}
                          maxLength={MAX_MESSAGE_LENGTH}
                        />
                        {canAttachFiles ? (
                          <DropzoneFormik
                            name="attachments"
                            label={t(
                              "common:contactPerformerModal.attachments"
                            )}
                            placeholder={t(
                              "common:contactPerformerModal.attachmentsPlaceholder"
                            )}
                            errorModalTexts={{
                              title: t("common:validation.file.error"),
                              close: t("common:button.label.close"),
                              tooManyFilesErrorMessage: t(
                                "common:validation.file.qty.limit",
                                {
                                  qty: MAX_FILE_SIZE,
                                }
                              ),
                              uploadErrorMessage: t(
                                "common:validation.file.uploadError"
                              ),
                            }}
                            multiple
                            acceptedFileTypes={[
                              ".png",
                              ".jpg",
                              ".jpeg",
                              ".gif",
                              ".pdf",
                              ".doc",
                              ".docx",
                            ]}
                            onDrop={uploadFiles}
                            maxFiles={MAX_FILE_SIZE}
                          />
                        ) : null}
                        <CheckBoxFormik
                          name="consent"
                          value={true}
                          label={t(
                            "common:contactPerformerModal.consentTextLabel"
                          )}
                        />
                      </Form>
                    </AlertMessage>
                    {showLoader && (
                      <div className="c-performer-contact__spinner">
                        <Spinner />
                      </div>
                    )}
                  </div>
                );
              }}
            </Formik>
          </Popup>
        )}
        <NotificationToast
          text={notificationText}
          type={notificationToast.type}
          stickToComponent={false}
          show={notificationToast.isOpen}
          handleHide={() =>
            setNotificationToast((state) => ({ ...state, isOpen: false }))
          }
          timeoutDuration={6000}
        />
      </>
    </Portal>
  );
};

export default PerformerContact;
